import { storeToRefs } from 'pinia'
import { useDocumentsStore } from '~/stores/documents'
import { useCasesStore } from '~/stores/cases'
import { useClientsStore } from '~/stores/clients'

/**
 * WHERE ELSE A DOCUMENT LIVES — the `Otros lugares` column (AlexAmin/marcus#169).
 *
 * ## What it is for
 *
 * The finder's worst state is six rows reading `Borrador de Convenio de Confidencialidad`. Three
 * things separate them, in order of how much work each does: the band above the row, this column for
 * the rows the band cannot separate, and the exact timestamp on hover for the residue. This is the
 * second of the three, and it is the reason the table earns its width.
 *
 * It is PLURAL on purpose. A singular "Fuente" column would have to name one canonical home per row,
 * which is the one-home-wins rule AlexAmin/marcus#168 deleted, smuggled back as a UI choice. #150
 * settled that a document has several homes; this names them.
 *
 * ## Why it is DERIVED and not a field on the row
 *
 * The obvious implementation is to ship `case_ids`/`client_ids`/`sheet_ids`/`conversation_ids` on
 * the flat listing and on search rows, the way `listOrgDocuments` already ships them. Two things are
 * wrong with that, and the second is a live trap the store already documents:
 *
 * 1. `listItems` rows go verbatim to an agent (`server/adk/tools/listUserDocuments.ts`), so four id
 *    arrays per row would be spent on a model's context for a column it cannot see.
 * 2. **Membership must not enter the document cache.** `fetchGroups` strips all four before
 *    `upsert` precisely because `upsert` shallow-merges and never drops fields — a cached `case_ids`
 *    would outlive every later write and go stale on the first link or unlink, which is a second
 *    copy of membership in the one cache that exists to prevent them.
 *
 * Everything needed is already client-side and already maintained: the cases store holds
 * `Case.documentIds`, and the documents store holds `clientDirectIds`, `sheetDocumentIds` and
 * `conversationDocumentIds` with their names. `fetchGroups` runs in Carpetas mode too (it has since
 * AlexAmin/marcus#145, so the signposts could say whether there was anything behind them), so this
 * index is populated in every one of the page's three states without a new request.
 *
 * ## What it can and cannot see, said out loud
 *
 * The org listing is capped at one page PER GROUP. A document beyond that cap contributes no
 * membership here, so the column can under-report on a very large group — it never over-reports, and
 * an empty cell is already the common, meaningful value. The alternative (a per-row server lookup at
 * org scope) is the O(N) subquery `listOrgDocuments`' own docblock rejects.
 */

export interface DocumentHome {
  /** The group id — the same key `ResolvedDocumentGroup.id` uses, so a band can exclude its own. */
  id: string
  kind: 'case' | 'client' | 'sheet' | 'conversation'
  name: string
}

export function useDocumentHomes() {
  const { t } = useI18n()
  const documents = useDocumentsStore()
  const casesStore = useCasesStore()
  const clientsStore = useClientsStore()
  const {
    clientDirectIds, sheetDocumentIds, sheetGroupsMeta,
    conversationDocumentIds, conversationGroupsMeta,
  } = storeToRefs(documents)
  const { byId: casesById } = storeToRefs(casesStore)
  const { byId: clientsById } = storeToRefs(clientsStore)

  /**
   * item id → its homes, in the canonical source order (client, case, sheet, thread — the
   * `DOCUMENT_SOURCES` order, so the column reads the same way the bands are stacked).
   *
   * Built once per dependency change rather than per row: the alternative is four lookups × N rows
   * on every render of a 100-row table.
   */
  const homesByDocument = computed(() => {
    const out = new Map<string, DocumentHome[]>()
    const push = (itemId: string, home: DocumentHome) => {
      const held = out.get(itemId)
      if (held) held.push(home)
      else out.set(itemId, [home])
    }

    for (const [clientId, ids] of clientDirectIds.value) {
      const name = clientsById.value.get(clientId)?.name
      if (!name) continue
      for (const id of ids) push(id, { id: clientId, kind: 'client', name })
    }
    for (const c of casesById.value.values()) {
      for (const id of c.documentIds) push(id, { id: c.id, kind: 'case', name: c.name })
    }
    for (const sheet of sheetGroupsMeta.value) {
      for (const id of sheetDocumentIds.value.get(sheet.id) ?? []) {
        push(id, { id: sheet.id, kind: 'sheet', name: sheet.name })
      }
    }
    for (const conv of conversationGroupsMeta.value) {
      for (const id of conversationDocumentIds.value.get(conv.id) ?? []) {
        // The same fallback `ChatPanel` and `IconRail` use for a titleless thread — a thread with no
        // title is a real thread that happens to be unnamed, not an absent home.
        push(id, { id: conv.id, kind: 'conversation', name: conv.name.trim() || t('common.untitled') })
      }
    }
    return out
  })

  /**
   * The homes to SHOW for one row: everything except the band it is already sitting under.
   *
   * `excludeGroupId` is the band's id, passed down by `BrowserGroupedList`. Without it the column
   * would restate the header directly above the row, which costs the width and says nothing — and in
   * Carpetas mode there is no band, so nothing is excluded and every home shows, which is the mode
   * where the column does the most work.
   */
  function homesFor(itemId: string, excludeGroupId?: string | null): DocumentHome[] {
    const all = homesByDocument.value.get(itemId)
    if (!all) return []
    return excludeGroupId ? all.filter(h => h.id !== excludeGroupId) : all
  }

  return { homesByDocument, homesFor }
}
